import { useState, useEffect } from "react";
import { nextMatchDate } from "@/mocks/games";

function getTimeLeft(target: Date) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl w-20 h-20 md:w-28 md:h-28 flex items-center justify-center">
          <span className="text-white font-black text-4xl md:text-6xl tabular-nums leading-none">
            {String(value).padStart(2, "0")}
          </span>
        </div>
      </div>
      <span className="text-white/60 text-xs md:text-sm uppercase tracking-widest mt-2 font-medium">
        {label}
      </span>
    </div>
  );
}

export default function HeroCountdown() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(nextMatchDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(nextMatchDate));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="countdown"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3c0e9133fbe8c7e8b73dbb55fbe99073.jpg"
          alt="Stadium background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 w-full max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6 animate-pulse">
          <span className="w-2 h-2 rounded-full bg-[#e8c34a]"></span>
          <span className="text-[#e8c34a] text-xs md:text-sm uppercase tracking-[0.3em] font-semibold">
            Next Match Countdown
          </span>
          <span className="w-2 h-2 rounded-full bg-[#e8c34a]"></span>
        </div>

        <h1 className="text-white font-black text-5xl md:text-8xl lg:text-9xl uppercase leading-none tracking-tight mb-4">
          WORLD
          <br />
          <span className="text-[#e8c34a]">CUP</span> 2026
        </h1>

        <p className="text-white/70 text-base md:text-lg max-w-xl mb-10 font-light leading-relaxed">
          The greatest tournament on Earth is coming. 48 teams, 104 games, one champion.
          <br />
          <span className="text-white font-medium">Mexico vs. South Africa — June 11, 2026</span>
        </p>

        {/* Countdown */}
        <div className="flex items-center gap-4 md:gap-8 mb-12">
          <CountUnit value={timeLeft.days} label="Days" />
          <span className="text-white/40 text-4xl md:text-5xl font-thin mb-6">:</span>
          <CountUnit value={timeLeft.hours} label="Hours" />
          <span className="text-white/40 text-4xl md:text-5xl font-thin mb-6">:</span>
          <CountUnit value={timeLeft.minutes} label="Minutes" />
          <span className="text-white/40 text-4xl md:text-5xl font-thin mb-6">:</span>
          <CountUnit value={timeLeft.seconds} label="Seconds" />
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <a
            href="#schedule"
            className="inline-flex items-center gap-2 bg-[#e8c34a] text-black font-bold px-8 py-4 rounded-full uppercase tracking-widest text-sm hover:bg-[#f0d060] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line"></i>
            View Full Schedule
          </a>
          <a
            href="#full90"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold px-8 py-4 rounded-full uppercase tracking-widest text-sm hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-beer-line"></i>
            The Full 90 Deal
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs uppercase tracking-widest">Scroll</span>
        <i className="ri-arrow-down-line text-white/40 text-xl"></i>
      </div>
    </section>
  );
}
